import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import axios from "axios";
import { Bell, CheckCheck, UserPlus, CheckCircle, XCircle, GitPullRequest, MessageSquare, Sparkles, Inbox } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcon = {
  application: { icon: UserPlus, color: "#FFB800" },
  accepted: { icon: CheckCircle, color: "#00F0FF" },
  rejected: { icon: XCircle, color: "#FF3B30" },
  change_request: { icon: GitPullRequest, color: "#FFB800" },
  change_response: { icon: Sparkles, color: "#A855F7" },
  message: { icon: MessageSquare, color: "#00F0FF" },
  general: { icon: Bell, color: "#A0A0B0" },
};

function timeAgo(iso, t) {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return t.notif.time_now;
  if (diff < 3600) return t.notif.time_min.replace("{n}", Math.floor(diff / 60));
  if (diff < 86400) return t.notif.time_hour.replace("{n}", Math.floor(diff / 3600));
  if (diff < 604800) return t.notif.time_day.replace("{n}", Math.floor(diff / 86400));
  return d.toLocaleDateString();
}

export default function NotificationDropdown({ user }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const unread = items.filter((n) => !n.read).length;

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setItems(res.data || []);
    } catch (_) {
      /* silent */
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    fetchItems();
    const i = setInterval(fetchItems, 30000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await fetchItems();
  };

  const markOneRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
    } catch (_) {
      /* silent */
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
    } catch (_) {
      /* silent */
    }
  };

  const handleClickItem = async (n) => {
    if (!n.read) markOneRead(n.id);
    setOpen(false);
    if (n.project_id) navigate(`/project/${n.project_id}`);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid="nav-notifications-btn"
        onClick={handleToggle}
        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors relative"
        title={t.notif.title}
        aria-label={t.notif.title}
      >
        <Bell className={`w-4 h-4 ${unread > 0 ? "text-[#00F0FF]" : "text-[#A0A0B0]"}`} />
        {unread > 0 && (
          <motion.span
            key={unread}
            initial={{ scale: 0.6 }}
            animate={{ scale: 1 }}
            data-testid="notif-unread-count"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-[#FF3B30] text-[9px] text-white font-bold"
          >
            {unread > 99 ? "99+" : unread}
          </motion.span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="notif-dropdown"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full right-0 mt-2 w-[360px] max-w-[90vw] glass-surface border border-white/10 rounded-xl overflow-hidden z-[60] shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-['Outfit'] text-sm font-semibold text-white">
                  {t.notif.title}
                </span>
                {unread > 0 && (
                  <span className="text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 rounded-full bg-[#FF3B30]/15 text-[#FF3B30]">
                    {unread} {t.notif.new}
                  </span>
                )}
              </div>
              {unread > 0 && (
                <button
                  data-testid="notif-mark-all-read"
                  onClick={markAllRead}
                  className="text-[11px] text-[#00F0FF] hover:text-white transition-colors flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" /> {t.notif.mark_all}
                </button>
              )}
            </div>

            {/* Items */}
            <div className="max-h-[420px] overflow-y-auto">
              {loading && items.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-[#606070] font-['JetBrains_Mono']">
                  {t.notif.loading}
                </div>
              ) : items.length === 0 ? (
                <div data-testid="notif-empty" className="px-4 py-10 text-center">
                  <Inbox className="w-8 h-8 text-[#606070] mx-auto mb-2" />
                  <p className="text-xs text-[#A0A0B0]">{t.notif.empty}</p>
                  <p className="text-[10px] text-[#606070] mt-1">
                    {t.notif.empty_sub}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-white/5">
                  {items.map((n) => {
                    const t = typeIcon[n.type] || typeIcon.general;
                    const Icon = t.icon;
                    return (
                      <li key={n.id}>
                        <button
                          data-testid={`notif-item-${n.id}`}
                          onClick={() => handleClickItem(n)}
                          className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors ${
                            !n.read ? "bg-[#00F0FF]/[0.04]" : ""
                          }`}
                        >
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{
                              background: `${t.color}15`,
                              border: `1px solid ${t.color}30`,
                            }}
                          >
                            <Icon className="w-3.5 h-3.5" style={{ color: t.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-xs leading-snug ${
                                n.read ? "text-[#A0A0B0]" : "text-white font-medium"
                              }`}
                            >
                              {n.message}
                            </p>
                            <span className="text-[10px] text-[#606070] font-['JetBrains_Mono'] mt-1 block">
                              {timeAgo(n.created_at, t)}
                            </span>
                          </div>
                          {!n.read && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] flex-shrink-0 mt-2" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
